import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import Utils from '@/common/utils/utils';
import { tr, trLabel, trTag } from '@/common/utils/tr';
import { NON_TRANSLATABLE_TAG_SYS_FIELDS } from '@/common/utils/translationUtils';

export const DRILLDOWN_DISPLAY_TYPE = {
    FIELD: 'FIELD',
    TAG: 'TAG',
    SYSTEM: 'SYSTEM',
    RAW: 'RAW'
};

const isIdentifier = value => [
    Constant.ID_PREFIX.PROJECT,
    Constant.ID_PREFIX.USER,
    Constant.ID_PREFIX.KNOWLEDGE
].includes(Utils.getPrefixOfId(value));

// Bookmark being created (temp id, not saved yet) shows its raw name; only saved bookmarks are translated
export const isTempBookmark = bookmark => Utils.getPrefixOfId(bookmark?.id) === Constant.BOOKMARK_PREFIX.TEMP;
export const getBookmarkDisplayName = bookmark => (isTempBookmark(bookmark) ? bookmark?.name : trTag(bookmark?.name));

export const translateByDisplayType = (value, displayType) => {
    if (typeof value !== 'string' || value.length === 0) return value;
    if (displayType === DRILLDOWN_DISPLAY_TYPE.FIELD) return trLabel(value);
    if (displayType === DRILLDOWN_DISPLAY_TYPE.TAG) return trTag(value);
    if (displayType === DRILLDOWN_DISPLAY_TYPE.SYSTEM) return tr(value);
    return value;
};

// telephone_call_handler value is either a USER id (never translated) or a KNOWLEDGE id
// (AI Operator, system-generated name that must be translated). Callers must check this
// BEFORE their own generic NON_TRANSLATABLE_TAG_SYS_FIELDS rule, which would otherwise force
// "no translation" for both cases since CALL_HANDLER is listed there for the USER case.
// Returns null when sysField isn't CALL_HANDLER, so callers can fall through to their own logic.
export const getCallHandlerDisplayType = (sysField, id) => {
    if (sysField !== TelephoneConstant.TELEPHONE.SYSTEM_FIELD.CALL_HANDLER) return null;
    return Utils.getPrefixOfId(id) === Constant.ID_PREFIX.KNOWLEDGE
        ? DRILLDOWN_DISPLAY_TYPE.SYSTEM
        : DRILLDOWN_DISPLAY_TYPE.RAW;
};

const getTagDisplayType = item => {
    if (item?.displayType) return item.displayType;

    const itemType = item?.itemType || item?.type;
    const itemId = item?.itemId || item?.id;
    const itemPrefix = Utils.getPrefixOfId(itemId);

    if (itemType === Constant.DRILLDOWN_TAG_ITEM_TYPE.MASTER_TAG_FIELD) {
        return DRILLDOWN_DISPLAY_TYPE.TAG;
    }
    if (
        itemType === Constant.DRILLDOWN_TAG_ITEM_TYPE.FORM ||
        itemType === 'field' ||
        [Constant.ID_PREFIX.FORM, Constant.ID_PREFIX.FIELD].includes(itemPrefix)
    ) {
        return DRILLDOWN_DISPLAY_TYPE.FIELD;
    }
    const callHandlerDisplayType = getCallHandlerDisplayType(item?.sysField, itemId);
    if (callHandlerDisplayType) return callHandlerDisplayType;
    if (
        NON_TRANSLATABLE_TAG_SYS_FIELDS.includes(item?.sysField) ||
        isIdentifier(item?.itemId || item?.id) ||
        [
            Constant.DRILLDOWN_TAG_ITEM_TYPE.PROJECT,
            Constant.DRILLDOWN_TAG_ITEM_TYPE.USER,
            Constant.DRILLDOWN_TAG_ITEM_TYPE.KNOWLEDGE
        ].includes(itemType)
    ) {
        return DRILLDOWN_DISPLAY_TYPE.RAW;
    }
    return DRILLDOWN_DISPLAY_TYPE.TAG;
};

const getSystemDisplayType = item => {
    if (item?.displayType) return item.displayType;
    if (item?.usage === Constant.DRILLDOWN_USAGE.STATUS || item?.isStatusField) {
        return DRILLDOWN_DISPLAY_TYPE.SYSTEM;
    }
    if (item?.usage === Constant.DRILLDOWN_USAGE.TAG) {
        return DRILLDOWN_DISPLAY_TYPE.FIELD;
    }
    if ([
        Constant.DRILLDOWN_TYPE_TEXT.FORM,
        Constant.DRILLDOWN_TYPE_TEXT.TICKET_TYPE,
        Constant.DRILLDOWN_TYPE_TEXT.PUBLIC_RANGE,
        Constant.DRILLDOWN_TYPE_TEXT.LIVE_OR_ARCHIVED,
        Constant.DRILLDOWN_TYPE_TEXT.EMAIL_TICKET_ORIGIN
    ].includes(item?.id || item?.type)) {
        return item?.id === Constant.DRILLDOWN_TYPE_TEXT.FORM
            ? DRILLDOWN_DISPLAY_TYPE.FIELD
            : DRILLDOWN_DISPLAY_TYPE.SYSTEM;
    }
    return DRILLDOWN_DISPLAY_TYPE.RAW;
};

const BOOKMARK_TYPES = [
    Constant.DRILLDOWN_TYPE_TEXT.PRIVATE_BOOKMARK,
    Constant.DRILLDOWN_TYPE_TEXT.SHARED_BOOKMARK
];

// Bookmark names are tags: an item is a bookmark when flagged or when its id/type is a bookmark field
const isBookmarkItem = item => item?.isBookmark === true || BOOKMARK_TYPES.includes(item?.id) || BOOKMARK_TYPES.includes(item?.type);

// Display type of an item value (not a field/parent label): bookmark names use TAG, others keep the system rule
const getSystemItemDisplayType = item => (isBookmarkItem(item) ? DRILLDOWN_DISPLAY_TYPE.TAG : getSystemDisplayType(item));

// Parent label is the field name (e.g. 個人用ブックマークタグ), so it keeps the system rule.
// Bookmark parent label is already translated (getSystemFieldNameInDrillDown), so it falls back to RAW
// to avoid translating it twice.
export const getDrilldownParentDisplayLabel = item =>
    translateByDisplayType(item?.label, item?.displayType || getSystemDisplayType(item));

export const getDrilldownChildDisplayLabel = (item, child) => {
    const fieldId = item?.id || item?.type;
    if (fieldId === Constant.FORMAT_OPTION_SEARCH.TICKET_CREATED_PROJECT_ID) {
        return child?.label;
    }

    const displayType = item?.usage === Constant.DRILLDOWN_USAGE.TAG
        ? getTagDisplayType({ ...item, ...child, itemId: child?.id })
        : getSystemItemDisplayType(item);
    return translateByDisplayType(child?.label, displayType);
};

export const getSelectedDrilldownItemDisplayLabel = item => {
    if (item?.id === Constant.FORMAT_OPTION_SEARCH.TICKET_CREATED_PROJECT_ID) {
        return item?.itemLabel;
    }

    const displayType = item?.isTagField
        ? getTagDisplayType(item)
        : getSystemItemDisplayType(item);
    return translateByDisplayType(item?.itemLabel, displayType);
};

export const getSelectedDrilldownFieldDisplayLabel = item => {
    if (item?.displayFieldType) {
        return translateByDisplayType(item?.fieldLabel, item.displayFieldType);
    }
    if (item?.isTagField || item?.isStatusField) {
        return translateByDisplayType(item?.fieldLabel, DRILLDOWN_DISPLAY_TYPE.FIELD);
    }
    return item?.fieldLabel;
};

export const getSelectedDrilldownOptionDisplayLabel = item =>
    `${getSelectedDrilldownItemDisplayLabel(item)}/${getSelectedDrilldownFieldDisplayLabel(item)}`;

export const getOptionDrilldownDisplayLabel = option => {
    const itemLabel = option?.itemLabel;
    const fieldLabel = option?.fieldLabel;
    if (itemLabel && fieldLabel) {
        const itemType = option?.usage === Constant.DRILLDOWN_USAGE.TAG
            ? getTagDisplayType(option)
            : getSystemItemDisplayType(option);
        const fieldType = option?.displayFieldType || (
            [Constant.DRILLDOWN_USAGE.TAG, Constant.DRILLDOWN_USAGE.STATUS].includes(option?.usage)
                ? DRILLDOWN_DISPLAY_TYPE.FIELD
                : DRILLDOWN_DISPLAY_TYPE.RAW
        );
        return `${translateByDisplayType(itemLabel, itemType)} ← ${translateByDisplayType(fieldLabel, fieldType)}`;
    }

    return option?.label || '';
};

export const getDrilldownTagItemDisplayType = item => getTagDisplayType(item);

export const getDrilldownItemDisplayType = item =>
    item?.usage === Constant.DRILLDOWN_USAGE.TAG
        ? getTagDisplayType(item)
        : getSystemItemDisplayType(item);

export const getDrilldownFieldDisplayType = item =>
    item?.displayFieldType || (
        item?.usage === Constant.DRILLDOWN_USAGE.TAG || item?.usage === Constant.DRILLDOWN_USAGE.STATUS
            ? DRILLDOWN_DISPLAY_TYPE.FIELD
            : DRILLDOWN_DISPLAY_TYPE.RAW
    );

export const getDisplayProjectNameWithTranslation = (projectDetail) => {
    if (Utils.isEmptyObject(projectDetail)) return '';

    const rawProjectName = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, projectDetail.name);
    if ([Constant.PROJECT_TYPE.BASE, Constant.PROJECT_TYPE.CUSTOMER].includes(projectDetail.type)) {
        return tr(rawProjectName);
    }

    return rawProjectName;
}

// telephone_call_handler value is either a USER id (human handler, never translated —
// identifier data) or a KNOWLEDGE/ticket id (AI Operator, display name is system-generated
// text and must be translated). Callers pass whichever id/name pair they currently hold.
export const getCallHandlerDisplayName = (id, name) => {
    if (typeof name !== 'string' || name.length === 0) return name;
    return Utils.getPrefixOfId(id) === Constant.ID_PREFIX.KNOWLEDGE ? tr(name) : name;
};
